// External imports
import { Col, Row, Tooltip } from 'react-bootstrap';
import { useCallback, useEffect, useRef, useState } from 'react';
import { TypedUseSelectorHook, useDispatch, useSelector } from 'react-redux';
// Redux imports
import { resetAssetsByAccount } from '../features/accounts/accountsSlice';
import { setToolTip, setNotifications, selectSettings } from '../features/application/appSlice';
import { resetTransactionsByAccount } from '../features/transactions/transactionsSlice';
// Component imports
// Function and misc imports
import { myAlgoApi } from '../api/myAlgoApi';
import { FETCH_TIMER } from '../utils/constants';
import type { RootState, AppDispatch } from '../store';
import arrowCurve from '../assets/images/arrow-curve.svg';

// Use throughout your app instead of plain `useDispatch` and `useSelector`
export const useAppDispatch = () => useDispatch<AppDispatch>();
export const useAppSelector: TypedUseSelectorHook<RootState> = useSelector;

// Hooks
export function usePrevious<T>(value: T): T {
  // The ref object is a generic container whose current property is mutable ...
  // ... and can hold any value, similar to an instance property on a class
  const ref: any = useRef<T>();
  // Store current value in ref
  useEffect(() => {
    ref.current = value;
  }, [value]); // Only re-run if value changes
  // Return previous value (happens before update in useEffect above)
  return ref.current;
}

export function useOnClickOutside(ref: any, handler: any) {
  useEffect(
    () => {
      const listener = (event: any) => {
        // Do nothing if clicking ref's element or descendent elements
        if (!ref.current || ref.current.contains(event.target)) {
          return;
        }
        handler(event);
      };
      document.addEventListener('mousedown', listener);
      document.addEventListener('touchstart', listener);
      return () => {
        document.removeEventListener('mousedown', listener);
        document.removeEventListener('touchstart', listener);
      };
    },
    // Add ref and handler to effect dependencies
    // It's worth noting that because the passed-in handler is a new ...
    // ... function on every render that will cause this effect ...
    // ... callback/cleanup to run every render. It's not a big deal ...
    // ... but to optimize you can wrap handler in useCallback before ...
    // ... passing it into this hook.
    [ref, handler]
  );
}

export function useDidUpdateEffect(fn: () => void, inputs: any[]) {
  const didMountRef = useRef(false);

  useEffect(() => {
    if (didMountRef.current) {
      return fn();
    }
    didMountRef.current = true;
  }, [inputs, fn]);
}

export function useLocalStorage<T>(key: string, initialValue: T) {
  // State to store our value
  // Pass initial state function to useState so logic is only executed once
  const [storedValue, setStoredValue] = useState<T>(() => {
    if (typeof window === 'undefined') {
      return initialValue;
    }

    try {
      // Get from local storage by key
      const item = window.localStorage.getItem(key);
      // Parse stored json or if none return initialValue
      return item ? JSON.parse(item) : initialValue;
    } catch (error) {
      // If error also return initialValue
      console.error(error);
      return initialValue;
    }
  });

  // Return a wrapped version of useState's setter function that ...
  // ... persists the new value to localStorage.
  const setValue = (value: T | ((val: T) => T)) => {
    try {
      // Allow value to be a function so we have same API as useState
      const valueToStore = value instanceof Function ? value(storedValue) : value;
      // Save state
      setStoredValue(valueToStore);
      // Save to local storage
      if (typeof window !== 'undefined') {
        window.localStorage.setItem(key, JSON.stringify(valueToStore));
      }
    } catch (error) {
      // A more advanced implementation would handle the error case
      console.error(error);
    }
  };

  return [storedValue, setValue] as const;
}

export const useIsMounted = () => {
  const isMountRef = useRef(true);
  useEffect(() => {
    isMountRef.current = false;
  }, []);
  return isMountRef.current;
};

export const useRefetch = () => {
  const dispatch = useAppDispatch();
  const refetch = (receivers?: any[]) => {
    dispatch(setNotifications({ message: 'Fetching from blockchain' }));
    // Refresh account will dispatch a notification that we're waiting 12 seconds while
    // blockchain updates. Then all accounts are invalidated and cause a refetch.
    if (!receivers) {
      setTimeout(() => {
        console.log('Fetching blockchain data for all accounts');
        dispatch(myAlgoApi.util.invalidateTags(['Accounts', 'Transactions', 'Assets']));
      }, FETCH_TIMER);
    }
    if (receivers?.length) {
      receivers.forEach((receiver: string) => {
        setTimeout(() => {
          console.log('Fetching blockchain data for address:', receiver);
          dispatch(
            myAlgoApi.endpoints.getAccountByAddress.initiate(receiver, {
              forceRefetch: true,
            })
          );
          dispatch(resetAssetsByAccount({ id: receiver }));
          dispatch(resetTransactionsByAccount({ id: receiver }));
        }, FETCH_TIMER);
        dispatch(myAlgoApi.util.invalidateTags(['Transactions']));
      });
    }
    // setTimeout(() => {
    //   dispatch(setNotifications({ message: '' }));
    // }, FETCH_TIMER);
  };
  return refetch;
};

export function useDebounce<T>(value: T, delay?: number): T {
  const [debouncedValue, setDebouncedValue] = useState<T>(value);

  useEffect(() => {
    const timer = setTimeout(() => setDebouncedValue(value), delay || 500);

    return () => {
      clearTimeout(timer);
    };
  }, [value, delay]);

  return debouncedValue;
}

export function useMonitor({
  timeout,
  warningTimeout,
  onWarn,
  onTimeout,
}: {
  timeout: number;
  warningTimeout: number;
  onWarn: () => void;
  onTimeout: () => void;
}) {
  const logoutTimeout = useRef<NodeJS.Timeout>();

  const logout = useCallback(() => {
    onTimeout && onTimeout();
  }, [onTimeout]);

  const warn = useCallback(() => {
    onWarn && onWarn();
    logoutTimeout.current = setTimeout(logout, timeout);
  }, [onWarn, logout, timeout]);

  useEffect(() => {
    let warnTimeout: NodeJS.Timeout;

    const setTimeouts = () => {
      warnTimeout = setTimeout(warn, warningTimeout);
    };

    const clearTimeouts = () => {
      if (warnTimeout) clearTimeout(warnTimeout);
      if (logoutTimeout.current) clearTimeout(logoutTimeout.current);
    };
    const events = ['load', 'mousemove', 'mousedown', 'click', 'scroll', 'keypress'];

    const resetTimeout = () => {
      clearTimeouts();
      setTimeouts();
    };

    for (let i in events) {
      window.addEventListener(events[i], resetTimeout);
    }

    setTimeouts();

    return () => {
      for (let i in events) {
        window.removeEventListener(events[i], resetTimeout);
        clearTimeouts();
      }
    };
  }, [logout, timeout, warn, warningTimeout]);
}

export const useRenderTooltip = () => {
  const [showTooltip, setShowTooltip] = useState(false);
  const { showAllTooltips } = useAppSelector(selectSettings);
  // const activeAccounts = useAppSelector( selectActiveAccounts, shallowEqual);
  // Show asset modal used here so each single asset in Dashboard doesnt need a modal
  // Same with response selectors
  // const hasValidAccount = useAppSelector( getHasValidAccount(state));
  // let url = useParams();
  const dispatch = useAppDispatch();
  // const isAnotherWindowOpen = activeAccounts.length || !hasValidAccount || url['*'] !== '';
  useEffect(() => {
    let timer1 = setTimeout(() => {
      setShowTooltip(true);
    }, 1500);
    return () => clearTimeout(timer1);
  }, []);
  const renderTooltip = (
    message: string,
    position?:
      | 'top-right'
      | 'right-down'
      | 'left-up'
      | 'right-up'
      | 'left-down'
      | 'bottom-left'
      | 'bottom-right',
    number?: number,
    onContinue?: () => void,
    ...props: any
  ) => {
    const formattedMessage = message.replace('\n', '<br>');
    return showTooltip && showAllTooltips ? (
      <Tooltip
        id={`tooltip-${number ? number : message}`}
        onClick={() => {
          if (typeof number === 'number') {
            dispatch(setToolTip(number + 1));
            onContinue && onContinue();
          }
        }}
        className="wallet-tooltip"
        {...props}
      >
        {position ? (
          <div className={`tooltip-custom-arrow-${position}`}>
            <img
              src={arrowCurve}
              width="40px"
              style={{
                filter: 'drop-shadow(0px 1px 1px rgb(0 225 255 / 0.4)) brightness(500%)',
              }}
              alt="arrow"
            />
          </div>
        ) : null}
        <div className="wallet-tooltip">
          {typeof number === 'number' ? (
            <Row style={{ display: 'flex', justifyContent: 'space-between' }}>
              <Col style={{ fontSize: '0.7rem', textAlign: 'left', color: 'rgb(135,138,142)' }}>
                Tutorial: {number + 1}/26
              </Col>
              <Col xs="auto" style={{ fontSize: '0.7rem' }}>
                <i className="fas fa-x" style={{ filter: 'brightness(500%)' }} />
              </Col>
              <hr style={{ margin: '0' }} />
            </Row>
          ) : null}
          <Row>
            <Col>{formattedMessage}</Col>
          </Row>
          {typeof number === 'number' ? (
            <Row>
              <Col
                xs="auto"
                style={{ fontSize: '0.7rem', color: 'rgb(135,138,142)', marginLeft: 'auto' }}
              >
                {number > 24 ? 'Finish' : 'Continue'}{' '}
                <i className="fas fa-chevron-right" style={{ filter: 'brightness(400%)' }} />
              </Col>
            </Row>
          ) : null}
        </div>
      </Tooltip>
    ) : (
      <></>
    );
  };
  return renderTooltip;
};
